import {
  BrowserModule,
  HAMMER_GESTURE_CONFIG,
} from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { NgModule, LOCALE_ID, Injectable } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import {
  HttpClientModule,
  HttpClient,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { RouterModule, PreloadAllModules } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToasterModule } from 'angular2-toaster';
import { CookieModule, CookieService } from 'ngx-cookie';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { PortalModule } from '@angular/cdk/portal';
import { StoreModule } from '@ngrx/store';
import * as fromApp from './app.reducer';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SortablejsModule } from 'angular-sortablejs';
import { SharedModule } from './shared/shared.module';
import { AccountModule } from './+account/account.module';
import { ROUTES } from './app.routes';
import { CanActivateApp } from './services/canActivateApp.service';
import { CanActivateLogin } from './services/canActivateLogin.service';
import { CanActivateLanding } from './services/canActivateLanding.service';
import { CanActivateStyleguide } from './services/canActivateStyleguide.service';
import { CurrentUser } from './services/currentUser.service';
import { UserAccount } from './services/userAccount.service';
import { AuthService } from './services/auth.service';
import { FacebookService } from './services/facebook.service';
import { FirebaseService } from './services/firebase.service';
import { LanguageService } from './services/language.service';
import { CountriesService } from './services/countries.service';
import { SalutationsService } from './services/salutations.service';
import { TooltipService } from './services/tooltip.service';
import { HammerConfig } from './services/hammerConfig.service';
import { StoriesService } from './+stories/list/list.service';
import { ShowStoryService } from './+stories/show/show.service';
import { ProjectService } from './services/project.service';
import { AuthInterceptor, HttpErrorInterceptor } from './interceptors';
import { DetectTouchService } from './services/detectTouch.serivce';
import { DetectPlatformService } from './services/detectPlatform.service';
import { GridLayoutService } from './services/grid-layout.service';
import { AppConfigService } from './services/app-config.service';
import { ImageViewerService } from './shared/image-viewer/image-viewer.service';
import { FileUploadModule } from 'ng2-file-upload';
import { AppComponent } from './app.component';
import { HeaderComponent } from './header/header.component';
import { AppHeaderComponent } from './header/appHeader/appHeader.component';
import { SessionHeaderComponent } from './header/sessionHeader/sessionHeader.component';
import '../styles/styles.scss';
import { includes } from 'lodash';
import { FacebookPixelService } from './services/facebook-pixel.service';
import { CanDeactivateShowStory } from './services/can-deactivate-show-story.service';
import { CanDeactivateUploadWizardStory } from './services/can-deactivate-upload-design-style.service';
import { CanDeactivateNewStory } from './services/can-deactivate-new-story.service';
import { CreditCardStorageSettingsService } from './services/credit-card-storage-settings.service';
import { PromotionsService } from './services/promotions.service';
import { PrintService } from './services/print.service';
import { OrdersService } from './services/orders.service';
import { NewStoryService } from './+stories/new';
import { RouterExtService } from './services/routerExt.service';
import { CanActivateContrib } from './services/canActivateContrib.service';
import { ShareService } from './services/share.service';
import { ProjectsModule } from './+projects/projects.module';
import { LandingModule } from './+landing/landing.module';
import { AddPhotosToGridService } from './services/add-photos-to-grid.service';
import { PhotoGridService } from './services/photo-grid.service';
import { SocialLoginModule, AuthServiceConfig, GoogleLoginProvider} from 'angularx-social-login';
import { environment } from 'environments/environment';

registerLocaleData(localeFr);

const APP_PROVIDERS = [
  CanActivateApp,
  CanActivateLogin,
  CanActivateLanding,
  CanActivateStyleguide,
  CanActivateContrib,
  LanguageService,
  CurrentUser,
  CookieService,
  UserAccount,
  AuthService,
  FacebookService,
  FirebaseService,
  StoriesService,
  ShowStoryService,
  ProjectService,
  CountriesService,
  SalutationsService,
  TooltipService,
  DetectTouchService,
  DetectPlatformService,
  GridLayoutService,
  AppConfigService,
  ImageViewerService,
  FacebookPixelService,
  CanDeactivateShowStory,
  CanDeactivateUploadWizardStory,
  CanDeactivateNewStory,
  CreditCardStorageSettingsService,
  PromotionsService,
  PrintService,
  OrdersService,
  RouterExtService,
  ShareService,
  AddPhotosToGridService,
  PhotoGridService
];

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function LocaleFactory(languageService: LanguageService) {
  const browserLang = languageService.getBrowserLang();
  const availableLangs = ['fr', 'en'];

  // Import locale (in head of this file), register it and add this lang to availableLangs array.
  return includes(availableLangs, browserLang) ? browserLang : 'en';
}

@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    HeaderComponent,
    AppHeaderComponent,
    SessionHeaderComponent,
  ],
  imports: [
    CookieModule.forRoot(),
    CommonModule,
    BrowserModule,
    FormsModule,
    HttpClientModule,
    FileUploadModule,
    RouterModule.forRoot(ROUTES, {
      preloadingStrategy: PreloadAllModules,
    }),
    StoreModule.forRoot(fromApp.appReducer),
    BrowserAnimationsModule,
    ToasterModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    SharedModule,
    PortalModule,
    AngularSvgIconModule,
    SortablejsModule.forRoot({}),
    AccountModule,
    LandingModule,
    ProjectsModule,
    SocialLoginModule,
  ],
  providers: [
    {
      provide: LOCALE_ID,
      deps: [LanguageService],
      useFactory: LocaleFactory,
    },
    APP_PROVIDERS,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig,
    },
    {
      provide: AuthServiceConfig,
      useFactory: provideConfig
    },
    NewStoryService,
  ],
  exports: [FileUploadModule],
})

export class AppModule {}
@Injectable()
export class FileUpload {}


const config = new AuthServiceConfig([
  {
    id: GoogleLoginProvider.PROVIDER_ID,
    provider: new GoogleLoginProvider(environment.googleLoginProviderUri)
  },
]);

export function provideConfig() {
  return config;
}

